import type { FactionId, GoodId, ZoneId } from './ids';

export interface FactionDef {
  id: FactionId;
  /** i18n key prefix: `faction.<id>.name`, `faction.<id>.short`. */
  i18nKey: `faction.${FactionId}`;
  /** Zone where the home planet is placed; `null` = any free orbit (GDD §3). */
  homeZone: ZoneId | null;
  /** Good this faction produces cheaply (comparative advantage, GDD §4). */
  cheapGood: GoodId;
  /** Goods the faction's population depends on importing. */
  needs: readonly GoodId[];
  /** Playable in the MVP (GDD §13). */
  inMvp: boolean;
  /** Ship silhouette family for the art pass. */
  shipStyle: 'classic' | 'angular' | 'scrapbuilt' | 'capsule';
}

export const FACTIONS: Readonly<Record<FactionId, FactionDef>> = {
  earthborn: {
    id: 'earthborn',
    i18nKey: 'faction.earthborn',
    homeZone: 'habitable',
    cheapGood: 'food',
    needs: ['alloys', 'helium3'],
    inMvp: true,
    shipStyle: 'classic',
  },
  reforged: {
    id: 'reforged',
    i18nKey: 'faction.reforged',
    homeZone: 'outer',
    cheapGood: 'helium3',
    needs: ['electronics'],
    inMvp: true,
    shipStyle: 'angular',
  },
  drifters: {
    id: 'drifters',
    i18nKey: 'faction.drifters',
    homeZone: 'hot',
    cheapGood: 'alloys',
    needs: ['food'],
    inMvp: false,
    shipStyle: 'scrapbuilt',
  },
  skyborn: {
    id: 'skyborn',
    i18nKey: 'faction.skyborn',
    homeZone: null,
    cheapGood: 'electronics',
    needs: ['food'],
    inMvp: false,
    shipStyle: 'capsule',
  },
};

export const MVP_FACTIONS: readonly FactionDef[] = Object.values(FACTIONS).filter((f) => f.inMvp);

/** At most this many players of one faction per match (GDD §3, rule 3). */
export const MAX_PLAYERS_PER_FACTION = 2;
